<template>
  <v-flex shrink class="main-view tall-content-wrapper">
    <oct-toolbar/>
    <v-row no-gutters class="cirrus-body-row">
      <v-col class="slice-controls d-flex">
        <oct-reference-image 
          :isLoading="refImgLoading || octDataLoading"
          :refImg="refImg"
          :refFit="refFit"
          :refSize="refSize"
          :refImgLocation="refImgLocation"
          :refImgNotFound="refImgNotFound"
          :sliceCurrentIndex="horzIndex"
          :slices="horzSlices"
          :sliceCurrent="horzCurrent"
          :composites="vertSlices"
          :compositeCurrent="vertCurrent"
          :sliceBounds="sliceBounds"
          :compositeBounds="compositeBounds"
          :isPlaying="isPlaying"
          :playRate="playRate"
          :showOverlay="true"
          :overlayOpacity="thicknessMapOpacity"
          :overlayImg="thicknessMapImg"
          :overlaySize="thicknessMapSize"
          :overlayColorSettings="thicknessMapColorSettings"
          :palette="thicknessMapPalette"
          :paletteSize="thicknessPaletteSize"
          :paletteMin="thicknessMapMinThicknessUm"
          :paletteMax="thicknessMapMaxThicknessUm"
          :paletteStep="thicknessMapThicknessStepUm"
          :showBindingBox="true"
          @previous="previousSlice"
          @next="nextSlice"
          @play="play"
          @pause="pause"
          @setPlayRate="setPlayRate"
          @selectSlice="selectSlice"
          @selectComposite="selectComposite" />
      </v-col>
      <v-col class="oct-slice-view-wrapper"
        @wheel.prevent="wheel">
        <oct-slice-view 
          :img="horzImg"
          :imgSize="sliceFit"
          :fitSize="sliceFit"
          :imgLoading="horzImgLoading"
          :dataLoading="octDataLoading"
          :imgNotFound="horzImgNotFound"
          :otherIndex="bottomIndex"
          :otherCount="bottomCount"
          :sliceInfo="sliceInfo"
          :sliceEyeMarker="sliceCurrentEyeMarker"
          :layerData="horzLayerData"
          :strechSegmentation="true"
          :octColorSettings="octColorSettings"
          otherColor="magenta"
          :showSegmentation="showSegmentation"
          :isMeasuring="isMeasuring" />
          <div class="image-index-counter">
            <index-counter :index="horzIndex" :count="horzCount" />
          </div>
      </v-col>
    </v-row>
    <v-row 
      no-gutters 
      class="cirrus-body-row"
      @wheel.prevent="wheel">
      <v-col class="slice-controls">
        <enface-view :enfaceFit="enfaceFit" :enfaceImgSize="enfaceImgSize" />
      </v-col>
      <v-col class="oct-slice-view-wrapper">
        <div class="image-index-counter">
            <index-counter :index="bottomIndex" :count="bottomCount" />
        </div>
        <oct-slice-view 
          slice-id="composite-image-wrapper"
          :img="bottomImg"
          :imgSize="compositeFit"
          :fitSize="compositeFit"
          :imgLoading="bottomImgLoading"
          :dataLoading="octDataLoading"
          :imgNotFound="bottomImgNotFound"
          :otherIndex="horzIndex"
          :otherCount="horzCount"
          :sliceInfo="compositeInfo"
          :sliceEyeMarker="compositeCurrentEyeMarker"
          :layerData="bottomLayerData"
          :strechSegmentation="true"
          :octColorSettings="octColorSettings"
          :flippedX="true"
          otherColor="cyan"
          :eyeMarkerAngle="-Math.PI / 2"
          :showSegmentation="showSegmentation"
          :isMeasuring="isMeasuring" />
      </v-col>
    </v-row>
  </v-flex>
</template>

<script>
import octToolbar from "./OCTToolbar";
import octReferenceImage from "./OCTReferenceImage";
import octSliceView from "./OCTSliceView";
import enfaceView from "./EnfaceView";
import indexCounter from "../IndexCounter";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import { mapWatchers } from "../../../helpers/utils";
import { defaultSize, getViewSize, setSize, setFittedSize, viewOffset, withinViewBounds, calculateRealLocation, defaultTransform, setCirrusRefereceFit, setTransformSizes } from "../../../canvas/geometry";
import { mapGetters } from "vuex";
export default {
  name: "cirrusBrowserBodyContent",
  components: { octToolbar, octReferenceImage, octSliceView, enfaceView, indexCounter },
  mixins: [responsiveCanvases],
  data(){
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      thicknessPaletteSize: defaultSize(),
      thicknessMapSize: defaultSize(),
      sliceFit: defaultSize(),
      compositeFit: defaultSize(),
      enfaceImgSize: defaultSize(),
      enfaceSize: defaultSize(),
      enfaceFit: defaultSize(),
      transform: defaultTransform(),
      referenceWrapperId: "reference-image-wrapper",
      thicknessWrapperId: "reference-palette-wrapper",
      sliceWrapperId: "slice-image-wrapper",
      compositeWrapperId: "composite-image-wrapper",
      enfaceWrapperId: "enface-image-wrapper"
    };
  },
  computed: {
    ...mapGetters([
      "refImg", "refImgLoading", "refImgNotFound",

      "sliceInfo", "slices", "sliceCurrent", "sliceBounds", "sliceCurrentIndex", 
      "sliceImg", "sliceImgNotFound", "sliceImgLoading", "sliceCurrentEyeMarker", 
      "sliceSegmentationLayerData",  

      "compositeInfo", "composites", "compositeCurrent", "compositeBounds", 
      "compositeCurrentIndex", "compositeImg", "compositeImgNotFound", "compositeImgLoading", 
      "compositeCurrentEyeMarker", "compositeSegmentationLayerData", 

      "octColorSettings", "octDataLoading", "showHD",

      "hdVerts", "hdVertImg", "hdVertImgNotFound", "hdVertImgLoading", "hdVertCurrentIndex",
      "hdVertSegmentationLayerData", "hdVertCurrent",

      "hdHorzs", "hdHorzImg", "hdHorzImgNotFound", "hdHorzImgLoading", "hdHorzCurrentIndex",
      "hdHorzSegmentationLayerData", "hdHorzCurrent",

      "isPlaying", "playRate",
      
      "thicknessMapImg", "thicknessMapColorSettings", "relativeMapSize",
      "thicknessMapPalette", "thicknessMapOpacity",
      "thicknessMapMinThicknessUm", "thicknessMapMaxThicknessUm", "thicknessMapThicknessStepUm",
      "showSegmentation", "isMeasuring",
      
      "isIE",
    
      "seriesType", "cirrusScanDisplayRatio", "siteCirrusScanDisplayRatio"]),
    horzImg() {
      return this.showHD ? this.hdHorzImg : this.sliceImg;
    },
    horzImgNotFound() {
      return this.showHD ? this.hdHorzImgNotFound : this.sliceImgNotFound;
    },
    horzImgLoading() {
      return this.showHD ? this.hdHorzImgLoading : this.sliceImgLoading;
    },
    horzIndex() {
      return this.showHD ? this.hdHorzCurrentIndex : this.sliceCurrentIndex;
    },
    horzCount() {
      return this.showHD ? this.hdHorzs.length : this.slices.length;
    },
    horzLayerData(){
      return this.showHD ? this.hdHorzSegmentationLayerData : this.sliceSegmentationLayerData;
    },
    horzKey(){
      return this.showHD ? "hdHorz" : "slice";
    },
    horzSlices() {
      return this.showHD ? this.hdHorzs : this.slices;
    },
    horzCurrent() {
      return this.showHD ? this.hdHorzCurrent : this.sliceCurrent;
    },
    bottomImg() {
      return this.showHD ? this.hdVertImg : this.compositeImg;
    },
    bottomImgNotFound() {
      return this.showHD ? this.hdVertImgNotFound : this.compositeImgNotFound;
    },
    bottomImgLoading() {
      return this.showHD ? this.hdVertImgLoading : this.compositeImgLoading;
    },
    bottomIndex() {
      return this.showHD ? this.hdVertCurrentIndex : this.compositeCurrentIndex;
    },
    bottomCount() {
      return this.showHD ? this.hdVerts.length : this.composites.length;
    },
    bottomLayerData(){
      return this.showHD ? this.hdVertSegmentationLayerData : this.compositeSegmentationLayerData;
    },
    bottomKey(){
      return this.showHD ? "hdVert" : "composite";
    },
    vertCurrent() {
      return this.showHD ? this.hdVertCurrent : this.compositeCurrent;
    },
    vertSlices() {
      return this.showHD ? this.hdVerts : this.composites;
    },
    ieStyle() {
      if (!this.isIE) return null;
      return "margin-top: 35px";
    },
    isMacularCube() {
      return this.seriesType == "macula";
    },
    scanDisplayRatio() {
      if (this.cirrusScanDisplayRatio && this.cirrusScanDisplayRatio != "default")
        return this.cirrusScanDisplayRatio;
      else
        return this.siteCirrusScanDisplayRatio;
    }
  },
  methods: {
     keyboardEvent(e){
      if(e.keyCode == 38) {
        this.previousSlice();
      }else if(e.keyCode == 39){
        this.nextComposite();         
      }else if(e.keyCode == 37){
        this.previousComposite();
      }else if(e.keyCode == 40){
        this.nextSlice();  
      }
    },
    nextComposite(){
      this.$store.dispatch("selectNextComposite");
    },
    previousComposite(){
      this.$store.dispatch("selectPreviouscomposite");
    },
    updateCanvasSizes() {
      // reference control size
      let rvs = getViewSize(this.referenceWrapperId);
      setSize(this.refSize, rvs);
      setCirrusRefereceFit(this.refSize, this.sliceBounds, this.refFit);

      let tms = getViewSize(this.thicknessWrapperId);
      setFittedSize(tms, tms, this.thicknessPaletteSize, 5);

      let thicknessMapSize = { w: this.refFit.w * this.relativeMapSize.w, h: this.refFit.h * this.relativeMapSize.h };
      setSize(this.thicknessMapSize, thicknessMapSize);

      let svs = getViewSize(this.sliceWrapperId);
      let cvs = getViewSize(this.compositeWrapperId);
      if (!this.isMacularCube || this.scanDisplayRatio == "stretch") {
        // stretch the slice images to fit within their wrapper
        setFittedSize(svs, svs, this.sliceFit, 15);

        // stretch the composite images to fit within their wrapper
        setFittedSize(cvs, cvs, this.compositeFit, 15);
      }
      else {
        let aspRatio = this.sliceInfo.dimensions.pixelWidth > this.sliceInfo.dimensions.pixelHeight ?
          (this.sliceInfo.dimensions.pixelHeight / this.sliceInfo.dimensions.pixelWidth) :
          (this.sliceInfo.dimensions.pixelWidth / this.sliceInfo.dimensions.pixelHeight);
        if (svs.w * aspRatio < svs.h) {
          setSize(this.sliceFit, { w: svs.w, h: svs.w * aspRatio });
          setSize(this.compositeFit, { w: svs.w, h: svs.w * aspRatio });
        } else {
          setSize(this.sliceFit, { w: svs.h / aspRatio, h: svs.h });
          setSize(this.compositeFit, { w: svs.h / aspRatio, h: svs.h });
        }
      }
      let evs = getViewSize(this.enfaceWrapperId);
      setSize(this.enfaceSize, evs);

      // enface image tends to be a wide rectangle, but should be rendered as a square
      setSize(this.enfaceImgSize, {w: 512, h: 512});
      setFittedSize(this.enfaceSize, this.enfaceImgSize, this.enfaceFit, 15);

      setTransformSizes(this.transform, this.refFit, this.refFit);
      console.log(this.seriesType);
    },
    refImgLocation(x, y){
      const offset = viewOffset(this.refSize, this.refFit);
      if (!withinViewBounds(x, y, this.refSize, offset)) return { x: -1, y: -1 };
      let loc = calculateRealLocation(x, y, offset, this.transform);
      return { x: loc.x / this.refFit.w, y: loc.y / this.refFit.h };
    },
    previousSlice() {
      if (!this.showHD) this.$store.dispatch("selectPreviousSlice");
    },
    nextSlice() {
      if (!this.showHD) this.$store.dispatch("selectNextSlice");
    },
    play() {
      if (!this.showHD) this.$store.dispatch("playSlices");
    },
    pause() {
      if (!this.showHD) this.$store.commit("pause");
    },
    setPlayRate(rate) {
      if (!this.showHD) this.$store.commit("setPlayRate", rate);
    },
    selectSlice(index){
      this.$store.dispatch("selectOCTImageByIndex", {keyword: "slice", index});
    },
    selectComposite(index){
      this.$store.dispatch("selectOCTImageByIndex", {keyword: "composite", index});
    },
    wheel(e) {
      if (e.deltaY > 0) this.previousSlice();
      else this.nextSlice();
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo", "relativeMapSize", "compositeInfo"], function() { 
        this.updateCanvasSizes();
      })
  },
  mounted(){
    this.updateCanvasSizes();
    document.addEventListener("keydown", this.keyboardEvent);
  },
  destroyed () {
    document.removeEventListener("keydown", this.keyboardEvent);
  }
};
</script>
